import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { MemoryRouter, Routes, Route, Navigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { AuthContext } from '../../context/AuthContext';
import PreviewRoleContext from '../../context/PreviewRoleContext';
import Dashboard from '../Dashboard';
import TimeTracking from '../TimeTracking';
import SafetyTraining from '../SafetyTraining';
import SafetyDocs from '../SafetyDocs';
import SupShop from '../SupShop';
import AdminWorkflows from './AdminWorkflows';
import SupBids from '../SupBids';
import SupProject from '../SupProject';
import AdminJobDetail from './AdminJobDetail';
import AdminPermits from './AdminPermits';
import AdminTimesheets from './AdminTimesheets';
import AdminPurchaseOrders from './AdminPurchaseOrders';
import JhaField from '../JhaField';
import ShopRequest from '../ShopRequest';
import ReportAccident from '../ReportAccident';
import PretripInspection from '../PretripInspection';
import CreatePO from '../CreatePO';
import ShopPos from '../ShopPos';
import WorkerProjectDetail from '../WorkerProjectDetail';
import AskAI from '../AskAI';
import Directives from '../Directives';

// The four roles you can preview. Each renders with a different mock login, so
// role-specific views differ as we build them out.
const ROLES = [
  { key: 'Worker',          label: 'Field',            first: 'Danny' },
  { key: 'ShopWorker',      label: 'Shop',             first: 'Charlie' },
  { key: 'FieldSupervisor', label: 'Field Supervisor', first: 'Alex' },
  { key: 'Accounting',      label: 'Admin',            first: 'Robin' },
  { key: 'ProjectManager',  label: 'Project Manager',  first: 'Sam' },
  { key: 'Executive',       label: 'Executive',        first: 'Ryan' },
];
function mockAuth(role) {
  const r = ROLES.find(x => x.key === role) || ROLES[0];
  return {
    worker: { id: 'preview', firstName: r.first, lastName: `(${r.label})`, fullName: `${r.first} (${r.label})`, role: r.key, canCreatePo: true },
    logout: () => {}, boards: [], members: [], loading: false, error: null,
  };
}

// Quick-jump shortcuts (they just set the starting screen; tiles navigate in-frame).
const JUMPS = [
  ['/dashboard', 'Home'], ['/time', 'Time'], ['/safety', 'Safety'], ['/safety-documents', 'Docs'],
  ['/jha', 'JHA'], ['/shop-request', 'Shop'], ['/report-accident', 'Accident'], ['/pretrip', 'Pre-Trip'], ['/create-po', 'PO'], ['/directives', 'Directives'],
];

// A self-contained worker app: its own MemoryRouter + mock login so navigation
// (tile taps, back buttons) stays inside the phone frame instead of leaving it.
function PreviewApp({ role, startPath }) {
  return (
    <MemoryRouter initialEntries={[startPath || '/dashboard']}>
      <AuthContext.Provider value={mockAuth(role)}>
        <PreviewRoleContext.Provider value={role}>
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/time" element={<TimeTracking />} />
          <Route path="/safety" element={<SafetyTraining />} />
          <Route path="/safety-documents" element={<SafetyDocs />} />
          <Route path="/sup/shop" element={<SupShop />} />
          <Route path="/sup/workflows" element={<AdminWorkflows embedded basePath="/sup/workflows" />} />
          <Route path="/sup/workflows/:type" element={<AdminWorkflows embedded basePath="/sup/workflows" />} />
          <Route path="/sup/bids" element={<SupBids />} />
          {/* The same job page a phone gets (Oct 21 2026 — the preview still showed the old one). */}
          <Route path="/sup/project/:id" element={<AdminJobDetail embedded />} />
          <Route path="/sup/jobs/:id" element={<AdminJobDetail embedded />} />
          <Route path="/sup/jobs/:id/:tile" element={<AdminJobDetail embedded />} />
          <Route path="/sup/old-job/:id" element={<SupProject />} />
          <Route path="/sup/permits" element={<AdminPermits embedded />} />
          <Route path="/sup/timesheets" element={<AdminTimesheets embedded />} />
          <Route path="/sup/purchase-orders" element={<AdminPurchaseOrders embedded />} />
          <Route path="/jha" element={<JhaField />} />
          <Route path="/shop-request" element={<ShopRequest />} />
          <Route path="/report-accident" element={<ReportAccident />} />
          <Route path="/pretrip" element={<PretripInspection />} />
          <Route path="/create-po" element={<CreatePO />} />
          <Route path="/shop/pos" element={<ShopPos />} />
          <Route path="/project/:id" element={<WorkerProjectDetail />} />
          <Route path="/ask" element={<AskAI />} />
          <Route path="/directives" element={<Directives />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </PreviewRoleContext.Provider>
      </AuthContext.Provider>
    </MemoryRouter>
  );
}

export default function MobilePreview() {
  const [role, setRole] = useState('Worker');
  const [startPath, setStartPath] = useState('/dashboard');
  const [nonce, setNonce] = useState(0); // bump to reset/re-mount the inner app
  const hostRef = useRef(null);

  // Render the preview into its own React root inside the phone frame. A fresh
  // host node per run keeps this safe under StrictMode's double-invoke.
  useEffect(() => {
    if (!hostRef.current) return;
    const node = document.createElement('div');
    node.style.cssText = 'position:absolute;inset:0;overflow-y:auto';
    hostRef.current.appendChild(node);
    const root = createRoot(node);
    root.render(<PreviewApp role={role} startPath={startPath} />);
    return () => { setTimeout(() => { try { root.unmount(); } catch {} node.remove(); }, 0); };
  }, [role, startPath, nonce]);

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Mobile Preview</h1>
          <p className="admin-page-sub">A live, clickable preview of the crew app — tiles and buttons navigate inside the phone. It loads data with your login.</p>
        </div>
      </div>

      {/* Role switcher */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Role:</span>
        {ROLES.map(r => (
          <button key={r.key} onClick={() => { setRole(r.key); setStartPath('/dashboard'); setNonce(n => n + 1); }}
            style={{ padding: '8px 14px', borderRadius: 20, border: 'none', background: role === r.key ? '#111' : '#f3f4f6', color: role === r.key ? '#e87722' : '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            {r.label}
          </button>
        ))}
      </div>

      {/* Quick jumps */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20, alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Jump to:</span>
        {JUMPS.map(([path, label]) => (
          <button key={path} onClick={() => { setStartPath(path); setNonce(n => n + 1); }}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: startPath === path ? '#e87722' : '#fff', color: startPath === path ? '#fff' : '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 24 }}>
        <div style={{ width: 390, height: 812, transform: 'translateZ(0)', position: 'relative', overflow: 'hidden', borderRadius: 44, border: '12px solid #111827', boxShadow: '0 24px 70px rgba(0,0,0,0.35)', background: '#f9fafb' }}>
          <div ref={hostRef} style={{ position: 'absolute', inset: 0 }} />
        </div>
      </div>
    </AdminLayout>
  );
}
